/**
 * OperatingGuardrails — the no-broker CONDUCT reminder, surfaced where an operator acts on a deal
 * (offer-menu builder, deal detail, blast composer). Motoveo is an advertising / lead-generation
 * platform, never a dealer/broker/party to a sale (docs/legal/TERMS-OF-SERVICE.md §1; the launch
 * council §4.A "Operational legal posture"). "No success fee" is not enough — the operator's conduct
 * must not look like brokering. This is a lightweight, NON-BLOCKING, collapsible note (native
 * <details>, no state, no gate); the full rules + do/don't scripts live in docs/GTM/OPERATING-GUARDRAILS.md.
 */

/** The universal no-broker conduct rules — identical wording across every surface (and the playbook doc). */
const RULES: string[] = [
  'Introductions only — connect the seller and the dealer, then step out of the transaction.',
  'Do NOT negotiate price for either party, and do NOT advise a seller to accept (that is broker/agent conduct).',
  'Do NOT represent the seller or the dealer — Motoveo acts for neither side.',
  'Do NOT touch funds, deposits, payoff, title, registration, or transport.',
  'Do NOT promise a "best price" or guarantee any outcome.',
  'Present dealer offers as advertising / lead responses, never as brokered bids.',
];

/** A one-line, context-specific framing for the screen the operator is on. */
const CONTEXT_NOTE: Record<NonNullable<Props['variant']>, string> = {
  offer:
    'The seller sees these as dealer-submitted offers (advertising responses). Curate and rank them — never negotiate, mark up, or steer the seller toward one.',
  deal: 'The seller and dealer transact directly. Record only what the parties report — never handle money, title, or the contract between them.',
  blast:
    'A blast is advertising broadcast to the whole opted-in franchise network — never routed to a chosen dealer. Each dealer bids and then deals directly with the seller. You are not soliciting bids on the seller’s behalf.',
};

interface Props {
  /** Tailors the one-line context note; omit for the generic reminder. */
  variant?: 'offer' | 'deal' | 'blast';
}

export function OperatingGuardrails({ variant }: Props) {
  return (
    <details className="guardrails">
      <summary>⚖ Operating guardrails — introductions only, never a broker</summary>
      {variant ? <p className="guardrails-context">{CONTEXT_NOTE[variant]}</p> : null}
      <ul>
        {RULES.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
      <p className="muted guardrails-foot">
        Full playbook + scripts: <code>docs/GTM/OPERATING-GUARDRAILS.md</code>. Consistent with the Terms of Service §1
        (advertising / lead-generation, not a party to any sale) and the no–success-fee model.
      </p>
    </details>
  );
}
